import './SoriginLogoBanner.css';

interface SoriginLogoBannerProps {
  className?: string;
}

export default function SoriginLogoBanner({ className }: SoriginLogoBannerProps) {
  return (
    <svg
      viewBox="0 0 480 120"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
      className={`sorigin-banner ${className ?? ''}`}
    >
      <defs>
        <linearGradient id="sb-tint" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8F2E2" />
          <stop offset="100%" stopColor="#F2EADB" />
        </linearGradient>

        <linearGradient id="sb-pole" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#006346" stopOpacity="0.72" />
          <stop offset="100%" stopColor="#076333" stopOpacity="0.55" />
        </linearGradient>

        <pattern id="sb-microline" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#006346" strokeOpacity="0.05" strokeWidth="0.4" />
        </pattern>
      </defs>

      {/* Layer 1 — faint mint parchment tint */}
      <rect width="480" height="120" fill="url(#sb-tint)" />

      {/* Layer 2 — diagonal micro-line hatch (very faint) */}
      <rect width="480" height="120" fill="url(#sb-microline)" />

      {/* Layer 3 — banknote frame */}
      <rect x="5" y="5" width="470" height="110" rx="2" fill="none" stroke="#006346" strokeOpacity="0.22" strokeWidth="0.8" />
      <rect x="10" y="10" width="460" height="100" rx="1" fill="none" stroke="#C9A961" strokeOpacity="0.40" strokeWidth="0.45" strokeDasharray="2 2.5" />

      {/* Layer 5 — corner flourishes */}
      <g fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.9" strokeLinecap="round">
        <path d="M 5 18 Q 5 5 18 5" />
        <path d="M 462 5 Q 475 5 475 18" />
        <path d="M 475 102 Q 475 115 462 115" />
        <path d="M 18 115 Q 5 115 5 102" />
      </g>
      <g fill="#C9A961" fillOpacity="0.90">
        <circle cx="11" cy="11" r="1.4" />
        <circle cx="469" cy="11" r="1.4" />
        <circle cx="469" cy="109" r="1.4" />
        <circle cx="11" cy="109" r="1.4" />
      </g>

      {/* Layer 6 — left rosette medallion (behind turbine) */}
      <g transform="translate(60, 60)" fill="none" stroke="#006346" strokeWidth="0.5">
        <circle r="44" strokeOpacity="0.12" />
        <circle r="38" strokeOpacity="0.18" strokeDasharray="0.6 2" />
        <g strokeOpacity="0.10">
          <ellipse rx="34" ry="20" />
          <ellipse rx="34" ry="20" transform="rotate(30)" />
          <ellipse rx="34" ry="20" transform="rotate(60)" />
          <ellipse rx="34" ry="20" transform="rotate(90)" />
          <ellipse rx="34" ry="20" transform="rotate(120)" />
          <ellipse rx="34" ry="20" transform="rotate(150)" />
        </g>
        <circle r="26" strokeOpacity="0.16" />
        <circle r="14" strokeOpacity="0.10" />
      </g>

      {/* Layer 7 — engraved wind turbine */}
      <g className="sb-turbine">
        {/* contact shadow */}
        <ellipse cx="60" cy="103" rx="11" ry="1.3" fill="#006346" fillOpacity="0.22" />

        {/* small ground hatching */}
        <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
          <line x1="48" y1="106" x2="72" y2="106" />
          <line x1="44" y1="109" x2="76" y2="109" strokeDasharray="1 2" />
          <line x1="50" y1="112" x2="70" y2="112" strokeDasharray="0.6 2" />
        </g>

        {/* tapered pole */}
        <path d="M 58.4 102.5 L 59.55 51 L 60.45 51 L 61.6 102.5 Z" fill="url(#sb-pole)" />

        {/* nacelle (housing behind hub) */}
        <path d="M 56 53 L 60 47 L 64 53 Z" fill="#006346" fillOpacity="0.6" />
        <rect x="57.5" y="48.5" width="5" height="3.5" rx="0.6" fill="#006346" fillOpacity="0.55" />

        {/* hub + 3 blades (rotation pivot at hub center) */}
        <g transform="translate(60, 50)">
          <g className="sb-turbine-blades">
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(120)" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(240)" />
          </g>
          {/* hub cap */}
          <circle r="2.8" fill="#C9A961" />
          <circle r="2.8" fill="none" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.5" />
          <circle r="1" fill="#006346" />
        </g>
      </g>

      {/* Layer 8 — tiny champagne sparkles (currency security-dot vibe) */}
      <g fill="#C9A961" fillOpacity="0.70">
        <circle cx="210" cy="18" r="0.9" />
        <circle cx="356" cy="102" r="0.9" />
        <circle cx="432" cy="28" r="0.7" />
        <circle cx="148" cy="98" r="0.7" />
      </g>

      {/* Layer 9 — denomination-style serial corners (faint text-like marks) */}
      <g fill="#006346" fillOpacity="0.35">
        <rect x="22" y="20" width="14" height="1" rx="0.5" />
        <rect x="22" y="22.5" width="9" height="0.8" rx="0.4" />
        <rect x="444" y="98" width="14" height="1" rx="0.5" />
        <rect x="449" y="100.5" width="9" height="0.8" rx="0.4" />
      </g>
    </svg>
  );
}
